import { FullValidationErrorFragment } from '@common/graphql/fragments';
import { gql } from '@common/hooks';

import { ExpiredComplianceItemsCount } from 'works/legacy/components/expired-compliance-items-count';

export const StaffingRequestAssignmentListQuery = gql`
  query StaffingRequestAssignmentListQuery(
    $state: [StaffingRequestAssignmentState!]
  ) {
    staffingRequestAssignments(state: $state) {
      id
      startDate
      state
      endDate
      lengthInWeeks
      shiftsPerWeek
      hoursPerShift
      shiftType

      extensionCount

      worksList {
        id
        completedItemsCount
        totalItemsCount
      }

      supplier {
        id
        name
      }

      worksProfile {
        id
        firstName
        lastName
        fullName
        email
        phone
        profilePic
      }

      staffingRequest {
        id
        externalId
        length
        billingRegularRate
        hiringLeader {
          id
          firstName
          lastName
        }

        organizationUnit {
          id
          name

          department {
            id
            name

            organizationBusiness {
              id
              name
            }
          }
        }

        role {
          id
          abbreviation
        }
      }
    }
  }
`;

export const UpdateStaffingRequestAssignmentMutation = gql`
  mutation UpdateStaffingRequestAssignmentMutation(
    $staffingRequestAssignmentId: ID!
    $input: UpdateStaffingRequestAssignmentInput!
  ) {
    updateStaffingRequestAssignment(
      staffingRequestAssignmentId: $staffingRequestAssignmentId
      input: $input
    ) {
      staffingRequestAssignment {
        id
        startDate
        endDate
        lengthInWeeks
        totalLengthInWeeks
        finalEndDate
        timeoffDescription
        originalTimeoffDescription
        shiftType
        shiftsPerWeek
        hoursPerShift
      }
      errors {
        ...FullValidationErrorFragment
      }
    }
  }
  ${FullValidationErrorFragment}
`;

export const PerformAssignmentAction = gql`
  fragment StaffingRequestAssignmentStateInfo on StaffingRequestAssignment {
    id
    cancelledReason
    cancelledDetail
    neverStarted
    finalEndDate

    canExtend {
      value
    }

    canOnboard {
      value
    }

    canOverrideClear {
      value
    }

    canViewClear {
      value
    }

    canWork {
      value
    }

    canComplete {
      value
    }

    canCancel {
      value
    }
  }

  mutation PerformAssignmentAction(
    $staffingRequestAssignmentId: ID!
    $input: PerformAssignmentActionInput!
  ) {
    performAssignmentAction(
      staffingRequestAssignmentId: $staffingRequestAssignmentId
      input: $input
    ) {
      staffingRequestAssignment {
        id
        state
        ...StaffingRequestAssignmentStateInfo
      }
    }
  }
`;

export const CancelAssignmentQuery = gql`
  query CancelAssignmentQuery($id: ID!) {
    staffingRequestAssignment(id: $id) {
      id
      worksProfile {
        id
        identificationCode
      }
    }
  }
`;

export type CancelAssignmentResponse = {
  staffingRequestAssignment: {
    id: string;
    worksProfile: {
      id: string;
      identificationCode: string | undefined;
    };
  };
};

export const PaginatedStaffingRequestsAssignmentsQuery = gql`
  query PaginatedStaffingRequestsAssignmentsQuery(
    $limit: Int!
    $offset: Int!
    $assignmentStates: [StaffingRequestAssignmentState!]
    $orderDirection: OrderDirection
    $orderField: StaffingRequestAssignmentOrderField
    $filters: StaffingRequestAssignmentFilterInput
    $staffingRequestTypes: [StaffingRequestType!]
  ) {
    paginatedStaffingRequestAssignments(
      limit: $limit
      offset: $offset
      assignmentStates: $assignmentStates
      orderDirection: $orderDirection
      orderField: $orderField
      filters: $filters
      staffingRequestTypes: $staffingRequestTypes
    ) {
      staffingRequestAssignments {
        id
        startDate
        endDate
        originalStartDate
        finalEndDate
        state
        billingRegularRate
        employmentType
        cancelledReason
        cancelledDetail
        extensionCount
        shiftsPerWeek
        hoursPerShift
        shiftType
        neverStarted
        organizationUnit {
          id
          name
          costCenter
          clinicalUnit {
            id
            displayName
          }
          organizationBusiness {
            id
            name
            city
            stateCode
          }
        }
        hiringLeader {
          id
          name
        }
        organization {
          id
          name
        }
        supplier {
          id
          name
        }
        organizationProfile {
          id
          employeeId
        }
        worksProfile {
          id
          firstName
          lastName
          fullName
          profilePic
          identificationCode
        }
        staffingRequest {
          id
          shiftType
          hoursPerShift
          shiftsPerWeek
          billingRegularRate
          code
          name
          externalId
          hiringLeader {
            id
            name
            firstName
            lastName
          }
          role {
            id
            name
            abbreviation
          }
          organizationUnit {
            id
            name
            costCenter
            clinicalUnit {
              id
              displayName
            }
          }
          organizationBusiness {
            id
            name
            stateCode
            city
          }
        }
        worksList {
          id
          completedItemsCount
          totalItemsCount
        }
        ...ExpiredComplianceItemsCountFragment

        canOnboard {
          value
        }

        canOverrideClear {
          value
        }

        canViewClear {
          value
        }

        canWork {
          value
        }

        canComplete {
          value
        }

        canCancel {
          value
        }

        canUpdate {
          value
        }

        activeExtensionOffer {
          id
          state
        }
      }
      totalCount
    }
  }
  ${ExpiredComplianceItemsCount.fragment}
`;
